import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import TransactionCard from '../components/TransactionCard'
import { ITEMS_PER_PAGE, buildSalesCsv, calculateSalesSummary, filterSales, fmtMoney, getPeriodStart, paginate } from '../lib/reportUtils'

const PERIODS = [
  ['today', 'Today'],
  ['week', 'Last 7 Days'],
  ['month', 'This Month'],
  ['all', 'All']
]

const PAYMENT_METHODS = ['all', 'Cash', 'EFTPOS', 'Credit']

export default function Reports() {
  const [period, setPeriod] = useState('today')
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('all')

  useEffect(() => { setPage(1) }, [period, query, paymentMethod])
  useEffect(() => { fetchSales() }, [period])

  const fetchSales = async () => {
    setLoading(true)
    let request = supabase.from('sales').select('*').order('created_at', { ascending: false })
    const from = getPeriodStart(period)
    if (from) request = request.gte('created_at', from.toISOString())

    const { data, error } = await request
    if (error) console.error('Error fetching reports', error)
    setSales(data || [])
    setLoading(false)
  }

  const filteredSales = useMemo(() => filterSales(sales, { query, paymentMethod }), [sales, query, paymentMethod])
  const summary = useMemo(() => calculateSalesSummary(filteredSales), [filteredSales])
  const paged = useMemo(() => paginate(filteredSales, page, ITEMS_PER_PAGE), [filteredSales, page])

  const exportCSV = () => {
    const csv = buildSalesCsv(filteredSales)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'spa-road-sales-' + period + (query ? '-filtered' : '') + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ padding: 16, paddingBottom: 80 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#1a3a2a' }}>Reports</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Search by customer, amount, item, payment or staff</div>
        </div>
        <button onClick={exportCSV} disabled={filteredSales.length === 0} style={{ background: '#fff', border: '1.5px solid #1a6b3c', color: '#1a6b3c', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: filteredSales.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredSales.length === 0 ? 0.5 : 1 }}>Export CSV</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto' }}>
        {PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => setPeriod(v)}
            style={{ flex: v === 'all' ? '0 0 auto' : 1, padding: '8px 10px', borderRadius: 8, border: period === v ? '2px solid #1a6b3c' : '1.5px solid #e5e7eb',
              background: period === v ? '#e8f5ee' : '#fff', color: period === v ? '#1a6b3c' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {l}
          </button>
        ))}
      </div>

      <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter this report by customer, item, amount, payment..."
        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 10, outline: 'none' }} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto' }}>
        {PAYMENT_METHODS.map(m => (
          <button key={m} onClick={() => setPaymentMethod(m)}
            style={{ padding: '7px 12px', borderRadius: 20, border: paymentMethod === m ? '2px solid #1a6b3c' : '1.5px solid #e5e7eb', background: paymentMethod === m ? '#e8f5ee' : '#fff', color: paymentMethod === m ? '#1a6b3c' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {m === 'all' ? 'All Payments' : m}
          </button>
        ))}
      </div>

      <div style={{ background: '#1a6b3c', color: '#fff', borderRadius: 12, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Total Revenue</div>
        <div style={{ fontWeight: 700, fontSize: 28 }}>{fmtMoney(summary.total)}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{summary.count} matching transactions</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        {['Cash', 'EFTPOS', 'Credit'].map(m => (
          <div key={m} style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{m}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a3a2a' }}>{fmtMoney(summary.byMethod[m] || 0)}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Loading...</div>
      ) : filteredSales.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10 }}>No matching transactions found</div>
      ) : (
        paged.rows.map(sale => <TransactionCard key={sale.id} sale={sale} />)
      )}

      {!loading && filteredSales.length > ITEMS_PER_PAGE && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={paged.page === 1} style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: paged.page === 1 ? '#f9fafb' : '#fff', color: paged.page === 1 ? '#9ca3af' : '#374151', fontSize: 13, fontWeight: 600, cursor: paged.page === 1 ? 'not-allowed' : 'pointer' }}>
            Previous
          </button>
          <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
            Page {paged.page} of {paged.totalPages}
          </div>
          <button onClick={() => setPage(p => Math.min(paged.totalPages, p + 1))} disabled={paged.page === paged.totalPages} style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: paged.page === paged.totalPages ? '#f9fafb' : '#fff', color: paged.page === paged.totalPages ? '#9ca3af' : '#374151', fontSize: 13, fontWeight: 600, cursor: paged.page === paged.totalPages ? 'not-allowed' : 'pointer' }}>
            Next
          </button>
        </div>
      )}
    </div>
  )
}
